import React, { useCallback } from 'react';
import { defineMessages, FormattedMessage } from 'react-intl';

import { cssMap } from '@atlaskit/css';
import { ModalBody, ModalTransition } from '@atlaskit/modal-dialog';
import { Flex } from '@atlaskit/primitives/compiled';
import Spinner from '@atlaskit/spinner';

import { LoadableLazy } from '@confluence/loadable';
import { useChoreographerAssets } from '@confluence/choreographer-services/entry-points/useChoreographerAssets.ts';
import { withFlags, type WithFlagsProps } from '@confluence/flags/entry-points/withFlags.ts';

const styles = cssMap({
	loadingContainer: {
		minHeight: '440px',
	},
});

const messages = defineMessages({
	errorTitle: {
		id: 'experiment-twcg-1489-cc-3p-connector-onboarding.setup-connector-modal.error-title',
		defaultMessage: 'Connector form could not be loaded',
		description: 'Title for the error flag shown when the connector setup form fails to load.',
	},
	errorDescription: {
		id: 'experiment-twcg-1489-cc-3p-connector-onboarding.setup-connector-modal.error-description',
		defaultMessage: 'Something went wrong. Please try again.',
		description:
			'Description for the error flag shown when the connector setup form fails to load.',
	},
});

const loadingContent = (
	<ModalBody hasInlinePadding={false}>
		<Flex xcss={styles.loadingContainer} justifyContent="center" alignItems="center">
			<Spinner size="xlarge" />
		</Flex>
	</ModalBody>
);

/**
 * Lazily loaded modal that renders the third-party connector connect form.
 *
 * ModalTransition and Modal are rendered here (in the eagerly-loaded chunk)
 * so the modal backdrop appears immediately while the connector form chunk
 * is loading. The spinner renders inside the modal shell during load.
 *
 * Uses @confluence/loadable (not React.lazy) for Confluence-standard code
 * splitting with SSR compatibility and built-in error handling.
 *
 * The package uses react-relay so Confluence's existing Relay environment
 * is used directly — no additional provider wrapping is needed.
 */
const LazyConnectorModalContent = LoadableLazy({
	loader: async () =>
		(
			await import(
				/* webpackChunkName: "loadable-twcg-1489-connector-modal" */
				'./SetupConnectorModalContent.tsx'
			)
		).SetupConnectorModalContent,
	loading: () => loadingContent,
});

export interface SetupConnectorModalProps {
	connectorKey: string;
	onClose: () => void;
}

function SetupConnectorModalWithFlags({
	connectorKey,
	onClose,
	flags,
}: SetupConnectorModalProps & WithFlagsProps) {
	const { Modal } = useChoreographerAssets();

	const onError = useCallback(() => {
		void flags.showErrorFlag({
			title: <FormattedMessage {...messages.errorTitle} />,
			description: <FormattedMessage {...messages.errorDescription} />,
			isAutoDismiss: true,
		});
		onClose();
	}, [flags, onClose]);

	return (
		<ModalTransition>
			<Modal onClose={onClose} width="large">
				<LazyConnectorModalContent
					connectorKey={connectorKey}
					onClose={onClose}
					loadingContent={loadingContent}
					onError={onError}
				/>
			</Modal>
		</ModalTransition>
	);
}

export const SetupConnectorModal = withFlags(SetupConnectorModalWithFlags);
